/**
 * Product Sales Report Hook
 *
 * Main hook for Product Sales Report feature using clean architecture
 */

import {useCallback} from 'react';
import {useProductSalesReportStore} from '../store/productSalesReportStore';
import {ProductSalesReportFactory} from '../store/productSalesReportFactory';
import {ProductSalesReportFilters} from '../models/productSalesReport.model';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

export const useProductSalesReport = () => {
  const store = useProductSalesReportStore();

  // Get product sales report
  const getProductSalesReport = useCallback(async (filters?: ProductSalesReportFilters) => {
    try {
      store.setLoading(true);
      store.setError(null);

      const useCase = ProductSalesReportFactory.getGetProductSalesReportUseCase();
      const reportData = await useCase.execute(filters);

      store.setReportData(reportData);
    } catch (error) {
      const errorHandlingService = StandardizedErrorHandler.getInstance();
      const appError = errorHandlingService.toAppError(error);
      store.setError(appError.message);
      errorHandlingService.handleError(error); // For notifications/logging
      throw appError; // Re-throw for error context
    } finally {
      store.setLoading(false);
    }
  }, []); // Remove store dependency to prevent infinite loops

  // Clear error
  const clearError = useCallback(() => {
    store.setError(null);
  }, []);

  // Reset store
  const reset = useCallback(() => {
    store.reset();
  }, []);

  return {
    // State
    ...store,

    // Actions
    getProductSalesReport,
    clearError,
    reset
  };
};

